<template>
  <v-form ref="form" class="">
    
    <BaseUtil
      ref="util"
      :pageLoading="pageLoading"
    />

    <v-tabs>
      <v-tab>Geral</v-tab>
      <v-tab>Conta a Pagar</v-tab>
      <v-tab>Ciclos Vinculados</v-tab>
      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="2">
            <MaskedTextField
              v-bind="getObject('code')"
              :inputData.sync="entry.code"
              mask-type="integer"
              label="Código"
              readonly
              :disabled="readOnly"
            />
          </v-col>

          <v-col cols="12" sm="10" md="6">
            <v-autocomplete
              v-model="entry.type"
              v-bind="getObject('type')"
              label="Tipo de Operação"
              :items="extraData.globalContext.allServiceProvisionTypes"
              item-value="value"
              item-text="text"
              :disabled="readOnly"
              @input="selectServiceProvisionType"
              autocomplete="off"
            />
            <!-- @input="selectOperationType(true)" -->
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <DateField
              :inputData.sync="entry.date"
              v-bind="getObject('date')"
              label="Data da Operação"
              :hasTodayButton="true"
              :hasTodayDate="true"
              :disabled="readOnly"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="12" sm="12" md="6">
            <v-autocomplete
              v-model="entry.patrimonyId"
              v-bind="getObject('patrimonyId')"
              label="Patrimônio"
              :items="extraData.accountContext.allPatrimonies"
              item-value="id"
              item-text="description"
              :disabled="readOnly"
              @input="selectPatrimony"
              autocomplete="off"
            >
              <template v-slot:append-outer>
                <PatrimonyNew
                  :inlineAddButton="true"
                  @entry-created="patrimonyCreated"
                  modelNameSingular="Patrimônio"
                />
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="6" sm="6" md="3" v-if="!loadingPatrimonies && entry.patrimonyId && getPatrimonyWorkingMetricType(entry.patrimonyId) == 'HOUR_METER'">
            <MaskedTextField
              v-bind="getObject('hourMeter')"
              :inputData.sync="entry.hourMeter"
              mask-type="integer"
              label="Horímetro"
              :disabled="readOnly"
              :key="entry.patrimonyId"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="6" sm="6" md="3" v-if="!loadingPatrimonies && entry.patrimonyId && getPatrimonyWorkingMetricType(entry.patrimonyId) == 'ODOMETER'">
            <MaskedTextField
              v-bind="getObject('odometer')"
              :inputData.sync="entry.odometer"
              mask-type="integer"
              label="Odômetro"
              :disabled="readOnly"
              :key="entry.patrimonyId"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="6" sm="6" md="3" v-if="entry.type == 'EXTERNAL_REFUELLING'">
            <MaskedTextField
              v-bind="getObject('fueledLiters')"
              :inputData.sync="entry.fueledLiters"
              mask-type="currency"
              label="Litragem Abastecida"
              :disabled="readOnly"
              suffix="L"
              :key="entry.type"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="12" class="">
            <ActivitiesIndex
              :activities="entry.activitiesAttributes"
              :readOnly="readOnly"
              :withPatrimonies="false"
              :initialDate="entry.date"
            />
          </v-col>

          <v-col cols="12" class="" v-if="entry.type && entry.type != 'EXTERNAL_REFUELLING'">
            <ConsumedItemsIndex
              :exitMovementItems="entry.exitMovementItemsAttributes"
              :serviceProvision="entry"
              :readOnly="readOnly"
              :isEdit="isEdit"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="entry.observations"
              v-bind="getObject('observations')"
              label="Observações"
              outlined
              :disabled="readOnly"
              autocomplete="off"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <DateField
              :inputData.sync="entry.cashOutflowAttributes.billDate"
              v-bind="getObject('billDate')"
              label="Data de Faturamento"
              :hasTodayButton="true"
              :hasTodayDate="true"
              :disabled="partialReadOnly || readOnly"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="12" sm="12" md="9">
            <v-autocomplete
              v-model="entry.cashOutflowAttributes.supplierId"
              v-bind="getObject('supplierId')"
              label="Pessoa Fornecedora do Serviço"
              :items="extraData.accountContext.allPeople"
              item-value="id"
              item-text="name"
              :disabled="partialReadOnly || readOnly"
              clearable
              @click:clear="$nextTick(() => entry.cashOutflowAttributes.supplierId = null)"
              autocomplete="off"
            >
              <template v-slot:append-outer>
                <PersonNew
                  :inlineAddButton="true"
                  :isSupplier="true"
                  @entry-created="personCreated"
                  modelNameSingular="Pessoa Fornecedora do Serviço"
                />
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="12" md="9">
            <!-- componente padrão do vuetify para textos -->
            <v-text-field
              v-model="selectedChartOfAccountName"
              v-bind="getObject('cashOutflowAttributes.chartId')"
              label="Plano de Contas"
              @click="clickChartOfAccount"
              ref="textFieldChartOfAccountRef"
              :class="'enabledTextFieldChartOfAccount'"
              clearable
              @click:clear="$nextTick(() => clearChartOfAccountSelected())"
              inputmode="none"
              :disabled="readOnly"
            />
          </v-col>

          <!-- componente desenvolvido por nós para a exibição e seleção dos planos de contas -->
          <ChartOfAccountSelect
            newTitle="Selecionar Plano de Contas"
            width="50%"
            newSaveButtonLabel="Confirmar"
            @select-confirm-action="chartOfAccountSelected"
            ref="chartOfAccountSelectRef"
            :loadActiveChartOfAccount="entry.cashOutflowAttributes.chart"
            :subtreesOfCodes="['4']"
          />

        </v-row>

        <v-row>
          <v-col cols="12" class="mt-3">
            <!-- Maneira que utilizamos para usar componentes nested. Primeiro chamamos o index do componente nested, dai o index traz as informações necessarias -->
            <PaymentIndex
              :payments="entry.cashOutflowAttributes.paymentsAttributes"
              :readOnly="partialReadOnly || readOnly"
              modelType="CashOutflow"
              :totalPayment.sync="totalPayment"
              @installment-update="$emit('installment-update', $event)"
              :hasTableTitle="true"
              tableTitle="Pagamentos"
            />
          </v-col>

        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6">
            <!-- outro exemplo de autocomplete -->
            <v-autocomplete
              v-model="harvestId"
              label="Filtrar Ciclos por Safra"
              :items="extraData.accountContext.allHarvests"
              item-value="id"
              item-text="name"
              @change="loadCulturesFromHarvest"
              clearable
              @click:clear="harvestId = null; cultureList = []; cultureId = null"
              autocomplete="off"
              :disabled="readOnly"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <!-- outro exemplo de autocomplete -->
            <v-autocomplete
              v-model="cultureId"
              label="Filtrar Ciclos por Cultura"
              :items="cultureList"
              item-value="id"
              item-text="name"
              :disabled="!harvestId || readOnly"
              clearable
              @click:clear="cultureId = null"
              @change="loadPeriodsFromHarvestAndCulture"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="12" md="6" >
            <v-autocomplete
              v-model="periodStatuses"
              label="Status dos Ciclos"
              :items="extraData.globalContext.allPeriodStatuses"
              item-value="value"
              item-text="text"
              clearable
              @click:clear="periodStatuses = []"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhum status for selecionado, todos serão exibidos"
              persistent-hint
              @change="loadPeriodsFromHarvestAndCulture"
            />
          </v-col>

          <v-col cols="12" class="">
            <!-- multiselect criado por nós, que representa aquele componente que utiliza 2 colunas para passar de um lado para outro. -->
            <MultiSelect
              v-model="entry.periodIds"
              v-bind="getObject('periodIds')"
              label="Ciclo(s)"
              :items="extraData.accountContext.allPeriodsEnabledForCostApportionment"
              :loadingItems="loadingPeriodsList"
              itemSubText="additionalInfo"
              itemSecondSubText="secondAdditionalInfo"
              :readOnly="readOnly"
            >
              <template v-slot:new_action> 
                <PeriodNew
                  :inlineAddButton="true"
                  @entry-created="periodCreated"
                  modelNameSingular="Ciclo"
                />
              </template>
            </MultiSelect>
          </v-col> 

        </v-row>
      </v-tab-item>
    </v-tabs>
  </v-form>
</template>

<script>
// importação de cada um dos componentes adicionais utilizados
// todos os componentes que não forem padrões do vuetify precisa ser importados aqui
import MultiSelect from "@shared_components/multi_select.vue";
import MaskedTextField from "@shared_components/masked_text_field.vue"
import DateField from "@shared_components/date_field.vue"
import PaymentIndex from "@models/payments/index.vue"
import ChartOfAccountSelect from "@models/chart_of_accounts/select.vue"
import ActivitiesIndex from "@models/activities/index.vue";
import ConsumedItemsIndex from "@models/consumed_items/index.vue";
import gql from 'graphql-tag';
import PatrimonyNew from "@models/patrimonies/new.vue"
import PersonNew from "@models/people/new.vue"
import BaseUtil from "@shared_components/base_util.vue"
import PeriodNew from "@models/periods/new.vue"

export default {
  data() {
    return {
      entryAttributes: {}, //armazena os atributos de cada campo
      totalPayment: 0, //exemplo de variavel que pode ser utilizada em todo form, nesse caso representa o total de pagamentos realizados em uma conta a pagar
      selectedChartOfAccountName: null, //exemplo de variavel, nesse caso representa o nome do plano de contas selecionado
      harvestId: null, //exemplo de variavel, nesse caso representa o id do harvest que foi criado
      cultureList: [], //lista de culturas, utilizado porque as culturas dependem de outra variavel, assim essa lista é dinamica e está sempre mudando
      cultureListLoading: false, //variavel para representar o carregamento da lista de culturas
      cultureId: null, // variavel para representar a cultura selecionada
      loadingPeriodsList: false, // variavel para representar o carregamento da lista de periods
      loadingPatrimonies: false,
      pageLoading: false,
      periodStatuses: [],
    };
  },
  props: {
    entry: Object, //objeto que vai conter todas as informações do form - objeto principal
    extraData: Object, // dados extras necessarios para 
    readOnly: { //campo que define o estado do form, no new e edit o readOnly é false, no show o readOnly é true
      type: Boolean,
      default: false //por default é falso
    },
    partialReadOnly: { // utilizado para alguns campos que fogem do padrão do readOnly
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    modelNameSingular: String,
  },
  //importação dos componentes adicionais
  components: {
    MultiSelect: MultiSelect,
    MaskedTextField: MaskedTextField,
    DateField: DateField,
    PaymentIndex: PaymentIndex,
    ChartOfAccountSelect: ChartOfAccountSelect,
    ActivitiesIndex: ActivitiesIndex,
    ConsumedItemsIndex: ConsumedItemsIndex,
    BaseUtil: BaseUtil,
    PatrimonyNew: PatrimonyNew,
    PersonNew: PersonNew,
    PeriodNew: PeriodNew
  },
  //funcao executada quando o form é criado
  created() {
    this.entryAttributes = this.setupEntryAttributes(this.entry);

    for (var payment in this.entry.cashOutflowAttributes.paymentsAttributes) {
      if (this.entry.cashOutflowAttributes.paymentsAttributes[payment] && this.entry.cashOutflowAttributes.paymentsAttributes[payment].value && !this.entry.cashOutflowAttributes.paymentsAttributes[payment]._destroy) {
        this.totalPayment += this.entry.cashOutflowAttributes.paymentsAttributes[payment].value;
      }
    }
    this.totalPayment = _.round(this.totalPayment, 2);

    if (this.entry.cashOutflowAttributes.chart) {
      this.selectedChartOfAccountName = this.entry.cashOutflowAttributes.chart.text;
    }

    if (this.periodStatuses.length == 0 && this.extraData.globalContext.allPeriodStatuses.length > 0) {
      this.periodStatuses = [1];
    }
  },
  //funcao executada quando o form é mounted
  mounted() {
  },
  // funções computadas
  computed: {
  },
  //declaração das funções
  methods: {
    // todo campo exibido no form utiliza esse método para pegar as meta-info do campo, como validações 
    getObject: function(element, component) {
      return this.entryAttributes[element];
    },
    getPatrimonyWorkingMetricType(patrimony_id) {
      let result_list = this.extraData.accountContext.allPatrimonies.filter(patrimony => patrimony.id == patrimony_id)
      if (result_list.length == 1) {
        return result_list[0].workingMetricType
      }
      return null;
    },
    selectPatrimony() { //patrimonio foi selecionado/alterado e as variaveis de horimetro e odometro sao resetadas de acordo
      // console.log(this.entry.odometer, this.entry.hourMeter)
      if (this.entry.patrimonyId) {
        this.loadingPatrimonies = true;
        if (this.getPatrimonyWorkingMetricType(this.entry.patrimonyId) == 'HOUR_METER') {
          this.entry.odometer = null;
        }
        else if (this.getPatrimonyWorkingMetricType(this.entry.patrimonyId) == 'ODOMETER') {
          this.entry.hourMeter = null;
        }
        else {
          this.entry.odometer = null;
          this.entry.hourMeter = null;
        }
        this.loadingPatrimonies = false;
      }
      // console.log(this.entry.odometer, this.entry.hourMeter)
    },
    selectServiceProvisionType() {
      if (this.entry.type) {
        if (this.entry.type != 'EXTERNAL_REFUELLING') {
          this.entry.fueledLiters = null;
        }
      }
    },
    //verifica se o form está valido, caso não estiver, já escrola a modal para o campo que deu erro
    isFormValid: function() {
      if (this.$refs.form.validate()) {
        return true;
      } else {
        setTimeout(function(){
          document.querySelector(".v-messages.error--text:first-of-type").scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      return false;
    },
    // reseta o form, limpando os campos e tirando as validações
    resetFormInputs: function() {
      this.$refs.form.resetValidation();
    },
    // método necessário para o componente do plano de contas
    // abre o modal que exibe o plano de contas e dá um blur no campo que exibe o plano de contas selecionado
    clickChartOfAccount: function() {
      this.$refs.chartOfAccountSelectRef.openDialog();
      this.$refs.textFieldChartOfAccountRef.blur();
    },
    // método executado quando o usuário seleciona o plano de contas
    // seta o id e o texto do campo
    chartOfAccountSelected: function(val) {
      // console.log(val);
      this.entry.cashOutflowAttributes.chart = val;
      this.entry.cashOutflowAttributes.chartId = val.id;
      this.selectedChartOfAccountName = val.text;
    },
    clearChartOfAccountSelected: function() {
      this.entry.cashOutflowAttributes.chart = null;
      this.entry.cashOutflowAttributes.chartId = null;
      this.selectedChartOfAccountName = null;
    },
    // exemplo de função utilizada para carregar uma lista de valores dinamicamente, de acordo com outros valores da interface
    loadCulturesFromHarvest: function() {
      if (this.harvestId){
        this.cultureListLoading = true;
        this.$apollo
          .query({
            query: CULTURES_FROM_HARVEST_QUERY,
            variables: {
              harvestId: this.harvestId
            },
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.cultureList = response.data.accountContext.allCulturesFromHarvest;
          })
          .catch(response => console.log(response))
          .finally(() => (this.cultureListLoading = false));
      }
      this.loadPeriodsFromHarvestAndCulture();
    },
    // outro exemplo de função para carregar valores de uma lista dinamicamente
    loadPeriodsFromHarvestAndCulture: function() {
      this.loadingPeriodsList = true;
      this.$apollo
        .query({
          query: PERIODS_FROM_HARVEST_AND_CULTURES_QUERY,
          variables: {
            harvestId: this.harvestId,
            cultureId: this.cultureId,
            periodStatuses: this.periodStatuses,
            serviceProvisionId: this.entry.id
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.extraData.accountContext.allPeriodsEnabledForCostApportionment = response.data.accountContext.allPeriodsEnabledForCostApportionment;
        })
        .catch(response => console.log(response))
        .finally(() => (this.loadingPeriodsList = false));
    },
    patrimonyCreated: function(patrimony) {
      //testar pra ver se o tipo é supplier
      // if (patrimony.patrimony_category.kind == "implements") {
      //   this.$refs.util.showErrorMessage("O patrimônio cadastrado é do tipo Implemento, por isso não irá aparecer na lista de patrimônios!");
      // } else {
      this.extraData.accountContext.allPatrimonies.unshift(patrimony);
      this.entry.patrimonyId = patrimony.id;
      this.entry.patrimony = patrimony;
      this.selectPatrimony();
      // }
    },
    personCreated: function(person) {
      //testar pra ver se o tipo é supplier
      // if (person.isCustomer) {
        this.extraData.accountContext.allPeople.unshift(person);
        this.entry.cashOutflowAttributes.supplierId = person.id;
        this.entry.cashOutflowAttributes.supplier = person;
      // } else {
      //   this.$refs.util.showErrorMessage("A pessoa cadastrada não é do tipo Cliente, por isso não irá aparecer na lista de clientes!");
      // }
    },
    periodCreated: function(period) {
      this.extraData.accountContext.allPeriodsEnabledForCostApportionment.unshift(period);
      this.entry.periodIds.push(period.id);
    }
  }
};

//exemplo de query utilizada para carregar valores dinamicamente, com passagem de parametros
const CULTURES_FROM_HARVEST_QUERY = gql `
  query ($harvestId: ID!) {
    accountContext {
      allCulturesFromHarvest(harvestId: $harvestId){
        id
        name
      }
    }
  }
`;

//outro exemplo de query utilizada para carregar valores dinamicamente, com passagem de parametros
const PERIODS_FROM_HARVEST_AND_CULTURES_QUERY = gql `
  query ($harvestId: ID, $cultureId: ID, $periodStatuses: [ID!], $serviceProvisionId: ID) {
    accountContext {
      allPeriodsEnabledForCostApportionment(harvestId: $harvestId, cultureId: $cultureId, periodStatuses: $periodStatuses, serviceProvisionId: $serviceProvisionId){
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
  }
`;

</script>

<style scoped>
  /* Exemplo de classe css utilizada em campos */
  ::v-deep .enabledTextFieldChartOfAccount input {
    cursor: pointer;
  }
</style>
